import type { DOMConversionMap, DOMConversionOutput, DOMExportOutput, LexicalNode } from "lexical";
import { $applyNodeReplacement, ElementNode } from "lexical";
import { convertToLettersOnly } from "../../../../../utils/convert-to-letters-only";
import type { UUID } from "../../../../../utils/uuid";
import "./dynamic-content-node.css";
import type { SerializedDynamicNode } from "./types";

function isDynamicContentElement(domNode: HTMLElement): boolean {
  return (
    domNode.tagName.toLowerCase() === "div" &&
    domNode.getAttribute("data-node-type") === "dynamic-content-node"
  );
}

export class DynamicContentNode extends ElementNode {
  __segment_id: UUID;
  // AWS don't allow - or to start with numbers in a handlebars variable name, so we need to
  // make a clean version of the segment id
  __clean_segment_id: string;
  __open: boolean;

  constructor(segment_id: UUID, open = true, key?: string) {
    super(key);
    this.__segment_id = segment_id;
    this.__clean_segment_id = DynamicContentNode.convertToLettersOnly(segment_id);
    this.__open = open;
  }

  static getType(): string {
    return "dynamic";
  }

  static convertToLettersOnly(input: string): string {
    return convertToLettersOnly(input);
  }

  static clone(node: DynamicContentNode): DynamicContentNode {
    return new DynamicContentNode(node.__segment_id, node.__open, node.__key);
  }

  static importJSON(serializedNode: SerializedDynamicNode): DynamicContentNode {
    const node = new DynamicContentNode(serializedNode.segment_id as UUID, serializedNode.open);
    return node;
  }

  exportJSON(): SerializedDynamicNode {
    return {
      ...super.exportJSON(),
      segment_id: this.__segment_id,
      clean_segment_id: this.__clean_segment_id,
      open: this.__open,
      type: "dynamic",
      version: 1,
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("div");
    element.setAttribute("data-node-type", "dynamic-content-node");
    element.setAttribute("data-segment-id", this.__segment_id);
    element.setAttribute("data-open", this.__open ? "true" : "false");
    element.classList.add("dynamic-container");
    element.classList.add(this.__open ? "dynamic-container--open" : "dynamic-container--closed");

    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (!isDynamicContentElement(domNode)) return null;

        return {
          conversion: (node: HTMLElement): DOMConversionOutput => {
            const segmentId = node.getAttribute("data-segment-id");
            const openAttr = node.getAttribute("data-open");
            const open = (openAttr ?? "true") === "true";

            if (!segmentId) {
              return { node: null };
            }

            const lexicalNode = new DynamicContentNode(segmentId as UUID, open);
            return { node: lexicalNode };
          },
          priority: 1,
        };
      },
    };
  }

  createDOM(): HTMLElement {
    const dom = document.createElement("div");
    dom.setAttribute("data-segment-id", this.__segment_id);
    dom.classList.add("dynamic-container");
    if (this.getOpen()) {
      dom.classList.add("dynamic-container--open");
    } else {
      dom.classList.add("dynamic-container--closed");
    }

    return dom;
  }

  updateDOM(prevNode: DynamicContentNode, dom: HTMLElement): boolean {
    if (prevNode.getSegmentId() !== this.getSegmentId()) {
      dom.setAttribute("data-segment-id", this.__segment_id);
    }

    if (this.getOpen()) {
      dom.classList.remove("dynamic-container--closed");
      dom.classList.add("dynamic-container--open");
    } else {
      dom.classList.add("dynamic-container--closed");
      dom.classList.remove("dynamic-container--open");
    }
    return false;
  }

  getCleanSegmentId(): string {
    return this.getLatest().__clean_segment_id;
  }

  getSegmentId(): UUID {
    return this.getLatest().__segment_id;
  }

  setSegmentId(segment_id: UUID): void {
    const writable = this.getWritable();
    writable.__segment_id = segment_id;
    writable.__clean_segment_id = DynamicContentNode.convertToLettersOnly(segment_id);
  }

  getOpen(): boolean {
    return this.getLatest().__open;
  }

  setOpen(open: boolean): void {
    const writable = this.getWritable();
    writable.__open = open;
  }

  canBeEmpty(): boolean {
    return false;
  }

  // Required override
  isShadowRoot(): boolean {
    // This makes the node behave *like* a root node internally for selection
    return true;
  }
}

export function $createDynamicContentNode(segment_id: UUID): DynamicContentNode {
  return $applyNodeReplacement(new DynamicContentNode(segment_id, true));
}

export function $isDynamicContentNode(
  node: LexicalNode | null | undefined,
): node is DynamicContentNode {
  return node instanceof DynamicContentNode;
}
